export type ShortcutAction =
  | 'invest'
  | 'resources'
  | 'technologies'
  | 'buildings'
  | 'empire'
  | 'alliances'
  | 'onu'
  | 'leaderboard';

export type ShortcutMap = Record<ShortcutAction, string>;

export const SHORTCUT_ORDER: ShortcutAction[] = [
  'invest',
  'resources',
  'technologies',
  'buildings',
  'empire',
  'alliances',
  'onu',
  'leaderboard',
];

export const SHORTCUT_LABELS: Record<ShortcutAction, string> = {
  invest: 'Investir (territoire sélectionné)',
  resources: 'Ressources',
  technologies: 'Technologies',
  buildings: 'Bâtiments',
  empire: 'Empire',
  alliances: 'Alliances',
  onu: 'ONU',
  leaderboard: 'Classement',
};

export const DEFAULT_SHORTCUTS: ShortcutMap = {
  invest: 'i',
  resources: 'r',
  technologies: 't',
  buildings: 'b',
  empire: 'e',
  alliances: 'a',
  onu: 'o',
  leaderboard: 'c',
};

const STORAGE_KEY = 'terra.keyboardShortcuts';

export function normalizeShortcutKey(raw: string): string | null {
  if (!raw) return null;
  if (raw.length === 1) {
    const k = raw.toLowerCase();
    if (/^[a-z0-9]$/.test(k)) return k;
    return null;
  }
  // Ignore named keys (Escape, Enter, Arrow…) for les raccourcis jeu.
  return null;
}

export function formatShortcutKey(key: string): string {
  return key.toUpperCase();
}

export function loadShortcuts(): ShortcutMap {
  const out: ShortcutMap = { ...DEFAULT_SHORTCUTS };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return out;
    const parsed = JSON.parse(raw) as Partial<Record<string, string>>;
    for (const action of SHORTCUT_ORDER) {
      const n = normalizeShortcutKey(String(parsed[action] ?? ''));
      if (n) out[action] = n;
    }
  } catch {
    /* keep defaults */
  }
  // Dédupliquer au chargement (conflits → défaut pour les suivants).
  const used = new Set<string>();
  for (const action of SHORTCUT_ORDER) {
    if (used.has(out[action])) {
      out[action] = DEFAULT_SHORTCUTS[action];
    }
    // Si le défaut est aussi pris, chercher une lettre libre.
    if (used.has(out[action])) {
      for (let i = 0; i < 26; i++) {
        const cand = String.fromCharCode(97 + i);
        if (!used.has(cand)) {
          out[action] = cand;
          break;
        }
      }
    }
    used.add(out[action]);
  }
  return out;
}

export function saveShortcuts(map: ShortcutMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/** Assigne une touche ; échange si déjà utilisée par une autre action. */
export function assignShortcut(map: ShortcutMap, action: ShortcutAction, key: string): ShortcutMap {
  const nextKey = normalizeShortcutKey(key);
  if (!nextKey) return map;
  const next = { ...map };
  const owner = SHORTCUT_ORDER.find((a) => a !== action && next[a] === nextKey);
  if (owner) {
    next[owner] = map[action];
  }
  next[action] = nextKey;
  return next;
}

export function panelForShortcutAction(
  action: ShortcutAction,
): 'resources' | 'technologies' | 'buildings' | 'empire' | 'alliances' | 'onu' | 'leaderboard' | null {
  if (action === 'invest') return null;
  return action;
}
